import { curry, entries, filter, map, pipe, some, toArray } from '@fxts/core';
import { MembershipClient } from '@marpple/mco-membership-core';
import { makeEmptyScreenNavigate } from '../../../shared/app/navigate';
import { navigateTo } from '../../../shared/app/util';
import { loading, loadingEnd } from '../../../shared/client/global';
import { Alert } from '../../../shared/components/cells/Alert/Alert';
import { Locals } from '../../../shared/type/locals';
import { preventEscapeErrorJson } from '../../../shared/util/preventEscapeErrorJson';
import { GoodsTypeId } from '../../Product/type';
import { MEMBERSHIP_SERVICE_URL } from '../api';
import { my_memberships_path } from '../constant';
import {
  Membership,
  MEMBERSHIP_BUY_PRODUCT_TYPE,
  MEMBERSHIP_PRODUCT_TYPE,
  MEMBERSHIP_STATE,
  MembershipProductType,
  MembershipStateType,
  ProductRewardType,
  REWARD_TYPES,
  RewardType,
} from '../type';

// 일단 만들었는데 sdk나오면 확인 후 올바르게 변경
export const toggleSpinner = (show: boolean): void => {
  if (show) {
    loading();
  } else {
    loadingEnd();
  }
};

/**
 * 멤버십 sdk에 넣을 client getter
 * @param token
 */
export const getSdkClient = (token?: string): MembershipClient => {
  return new MembershipClient({ apiKey: token, origin: MEMBERSHIP_SERVICE_URL });
};

export const activateSdk = async (client: MembershipClient) => {
  await client.activate();
  return client;
};

/**
 * 멤버십 sdk 토큰 만료 이벤트 달기
 * @param client
 */
export const addExpiredMembershipTokenEvent = (client: MembershipClient) => {
  client.on('expired_token', () => {
    Alert.open(
      preventEscapeErrorJson({
        title: ET('mps2::membership::error::token_expired_title'),
        message: ET('mps2::membership::error::token_expired_description'),
      }),
    ).then(() => {
      window.location.reload();
    });
  });
  return client;
};

/**
 * 멤버십 sdk에 activate 후 토큰 만료 이벤트 (프론트에서만 사용)
 * @param token
 */
export const getActivatedSdkWithEvent = (token?: string) =>
  pipe(getSdkClient(token), activateSdk, addExpiredMembershipTokenEvent);

/**
 * SDK CALLBACK: 멤버십 리스트로 가기
 */
export const onGoBackToMembershipSupportListButtonClick = () => {
  navigateTo(`/${ET.lang}/@/${my_memberships_path}`, makeEmptyScreenNavigate);
};

const GOODS_TYPE_MEMBERSHIP_PRODUCT_TYPE = {
  MPS: MEMBERSHIP_PRODUCT_TYPE.GOODS,
  PBG: MEMBERSHIP_PRODUCT_TYPE.ORDER_MADE,
  TPL: MEMBERSHIP_PRODUCT_TYPE.ORDER_MADE,
  DTG: MEMBERSHIP_PRODUCT_TYPE.DIGITAL_GOODS,
} as const;

export const convertProductRewardToGoodsTypeIds = (product_reward: ProductRewardType): GoodsTypeId[] => {
  // 매핑 정보 일단 여기 안으로 격리
  const GOODS_TYPE_MEMBERSHIP_PRODUCT_TYPE = {
    MPS: MEMBERSHIP_BUY_PRODUCT_TYPE.BUY_GOODS,
    PBG: MEMBERSHIP_BUY_PRODUCT_TYPE.BUY_ORDER_MADE,
    TPL: MEMBERSHIP_BUY_PRODUCT_TYPE.BUY_ORDER_MADE,
    DTG: MEMBERSHIP_BUY_PRODUCT_TYPE.BUY_DIGITAL_GOODS,
  } as const;

  return pipe(
    entries(GOODS_TYPE_MEMBERSHIP_PRODUCT_TYPE),
    filter(([, reward]) => reward === product_reward),
    map(([goods_type_id]) => goods_type_id),
    toArray,
  );
};

/**
 * goods_type_id를 통해 멤버십 상품 타입을 얻습니다.
 */
export const getMembershipProductTypeByGoodsTypeId = (goods_type_id: GoodsTypeId): MembershipProductType => {
  return GOODS_TYPE_MEMBERSHIP_PRODUCT_TYPE[goods_type_id];
};

export const getGoodsTypeIdsByMembershipProductTypeId = (
  membership_product_type_id?: MembershipProductType,
): GoodsTypeId[] | undefined => {
  if (!membership_product_type_id) {
    return undefined;
  }

  return pipe(
    GOODS_TYPE_MEMBERSHIP_PRODUCT_TYPE,
    entries,
    filter(([, mpi]) => mpi === membership_product_type_id),
    map(([goods_type_id]) => goods_type_id),
    toArray,
  );
};

/**
 * goods_type_id를 통해 멤버십 서비스에 매핑되는 reward를 얻습니다.
 * @param goods_type_id
 */
export const getRewardByGoodsTypeId = (
  goods_type_id: GoodsTypeId,
): keyof Pick<typeof REWARD_TYPES, 'BUY_GOODS' | 'BUY_ORDER_MADE' | 'BUY_DIGITAL_GOODS'> => {
  return {
    MPS: REWARD_TYPES.BUY_GOODS,
    PBG: REWARD_TYPES.BUY_ORDER_MADE,
    TPL: REWARD_TYPES.BUY_ORDER_MADE,
    DTG: REWARD_TYPES.BUY_DIGITAL_GOODS,
  }[goods_type_id];
};

export const getRewardsByGoodsTypeIds = (
  goods_type_ids: readonly GoodsTypeId[],
): Array<keyof Pick<typeof REWARD_TYPES, 'BUY_GOODS' | 'BUY_ORDER_MADE' | 'BUY_DIGITAL_GOODS'>> => {
  return pipe(
    goods_type_ids,
    map((id) => getRewardByGoodsTypeId(id)),
    toArray,
  );
};

/**
 * 멤버십에 특정 reward가 있는지 확인하는 함수
 */
export const hasRewardType = curry(<M extends Membership>(reward_type: RewardType, membership: M) => {
  return some((r) => r.type === reward_type, membership.rewards);
});

/**
 * `login_user`에서 `membership_project_id` 추출하는 함수
 * @param login_user
 */
export const extractMembershipProjectId = (login_user: Locals['login_user']) => {
  return login_user?._?.store?.membership_project_id;
};

/**
 * "공개 멥버십 플랜"인지 확인하는 유틸 함수
 */
export const isOpenMembership = (membership: { state: MembershipStateType }): boolean => {
  return membership.state === MEMBERSHIP_STATE.OPEN;
};

/**
 * "비공개 멥버십 플랜"인지 확인하는 유틸 함수
 */
export const isHiddenMembership = (membership: { state: MembershipStateType }): boolean => {
  return membership.state === MEMBERSHIP_STATE.HIDDEN;
};

/**
 * "삭제 멤버십 플랜"인지 확인하는 유틸 함수
 */
export const isDeletedMembership = (membership: { state: MembershipStateType }): boolean => {
  return membership.state === MEMBERSHIP_STATE.DELETED;
};

export const isProductRewardExist = (membership: { rewards: { type: ProductRewardType }[] }): boolean => {
  return membership.rewards.length > 0;
};
